import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import {
  isPlausibleDate,
  plausibleMaxDate,
  plausibleMinDate,
} from '../lib/date-bounds';
import moment, { type Moment } from 'moment';
import { EditOutlined } from '@mui/icons-material';

type SelectOption = {
  label: string;
  value: string | number;
};

export type EditableField =
  | {
      type: 'text';
      value: string;
      placeholder?: string;
      onSave: (next: string) => void | Promise<void>;
    }
  | {
      type: 'number';
      value: number | null;
      placeholder?: string;
      onSave: (next: number | null) => void | Promise<void>;
    }
  | {
      type: 'select';
      value: string | number;
      options: SelectOption[];
      onSave: (next: string | number) => void | Promise<void>;
    }
  | {
      type: 'multi-select';
      value: (string | number)[];
      options: SelectOption[];
      onSave: (next: (string | number)[]) => void | Promise<void>;
    }
  | {
      type: 'date';
      value: Moment | Date | string | null;
      onSave: (next: Moment | null) => void | Promise<void>;
    }
  | {
      type: 'custom';
      onEdit: () => void;
    }
  | {
      /** Render a fully custom inline editor. Receives a `close` callback that returns the row to display mode. */
      type: 'render';
      renderEditor: (ctx: { close: () => void }) => React.ReactNode;
    };

export type EditableDataListViewItem = {
  title: string;
  value: React.ReactNode;
  edit?: EditableField;
};

export type EditableDataListViewSection = {
  title: string;
  rows: EditableDataListViewItem[];
};

type EditableDataListViewProps = {
  /** Flat list of items rendered as label/value pairs. */
  data?: EditableDataListViewItem[];
  /** Sections rendered as a section header followed by label/value pairs. Use this when you want multiple sections to share one grid (so column widths align). Mutually exclusive with `data`. */
  sections?: EditableDataListViewSection[];
  /** Optional fixed width for the label column. When omitted, the column auto-sizes to the widest label. */
  labelWidth?: number | string;
  /** Number of label/value pairs to render side-by-side. Default 1. */
  columns?: number;
};

const toMoment = (value: Moment | Date | string | null): Moment | null => {
  if (value === null || value === undefined || value === '') return null;
  const m = moment(value as moment.MomentInput);
  return m.isValid() ? m : null;
};

// Sizing for inline editors. Matches the cell's display height (32px minHeight
// minus 4px vertical padding × 2 = 24px content area) so view → edit doesn't
// reflow the row.
const EDITOR_INPUT_HEIGHT = 24;

const EDITOR_SX = {
  width: '100%',
  height: EDITOR_INPUT_HEIGHT,
  '& .MuiOutlinedInput-root': {
    height: EDITOR_INPUT_HEIGHT,
    minHeight: EDITOR_INPUT_HEIGHT,
    fontSize: '0.8125rem',
    backgroundColor: 'transparent',
    paddingRight: 0,
    '& fieldset': {
      borderColor: 'primary.main',
      borderWidth: 1,
      borderRadius: '4px',
    },
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.8125rem',
    padding: '0 8px',
    height: `${EDITOR_INPUT_HEIGHT}px !important`,
    minHeight: `${EDITOR_INPUT_HEIGHT}px !important`,
    boxSizing: 'border-box',
    lineHeight: `${EDITOR_INPUT_HEIGHT}px`,
  },
  '& .MuiSelect-select': {
    fontSize: '0.8125rem',
    minHeight: '0 !important',
    height: `${EDITOR_INPUT_HEIGHT}px !important`,
    padding: '0 28px 0 8px !important',
    boxSizing: 'border-box',
    lineHeight: `${EDITOR_INPUT_HEIGHT}px`,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': { right: 4 },
  // MUI X DatePicker (with enableAccessibleFieldDOMStructure=false)
  // renders a plain <input>; covered by MuiInputBase-input above. We also
  // tame the calendar adornment so it fits within the 24px row.
  '& .MuiInputAdornment-root': {
    height: EDITOR_INPUT_HEIGHT,
    maxHeight: EDITOR_INPUT_HEIGHT,
    marginLeft: 0,
    marginRight: '2px',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    padding: 2,
    '& .MuiSvgIcon-root': { fontSize: '0.95rem' },
  },
} as const;

const EditableRow: React.FC<{
  item: EditableDataListViewItem;
  /** When true, applies a left margin on the label cell at `sm+` to create a gap between column-pairs. No-op on `xs`. */
  spaceFromPrev?: boolean;
}> = ({ item, spaceFromPrev }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [draft, setDraft] = useState<string>('');
  const [multiDraft, setMultiDraft] = useState<(string | number)[]>([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const editable = item.edit;

  useEffect(() => {
    if (!isEditing || !editable) {
      setSelectOpen(false);
      return;
    }
    if (editable.type === 'text' || editable.type === 'number') {
      setDraft(
        editable.value === null || editable.value === undefined
          ? ''
          : String(editable.value),
      );
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (editable.type === 'multi-select') {
      setMultiDraft(editable.value);
      setSelectOpen(true);
    } else if (editable.type === 'select') {
      setSelectOpen(true);
    }
  }, [isEditing, editable]);

  const commitText = async () => {
    if (!editable || editable.type !== 'text') return;
    setIsEditing(false);
    if (draft !== editable.value) await editable.onSave(draft);
  };

  const commitNumber = async () => {
    if (!editable || editable.type !== 'number') return;
    setIsEditing(false);
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && Number.isNaN(next)) return;
    if (next !== editable.value) await editable.onSave(next);
  };

  const cancel = () => setIsEditing(false);

  const beginEdit = () => {
    if (!editable) return;
    if (editable.type === 'custom') {
      editable.onEdit();
      return;
    }
    setIsEditing(true);
  };

  const renderEditor = () => {
    if (!editable) return null;

    if (editable.type === 'text' || editable.type === 'number') {
      const isNumber = editable.type === 'number';
      return (
        <TextField
          inputRef={inputRef}
          size="small"
          fullWidth
          variant="outlined"
          type={isNumber ? 'number' : 'text'}
          value={draft}
          placeholder={editable.placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={isNumber ? commitNumber : commitText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (isNumber) commitNumber();
              else commitText();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          sx={EDITOR_SX}
        />
      );
    }

    if (editable.type === 'select') {
      return (
        <Select
          autoFocus
          open={selectOpen}
          size="small"
          fullWidth
          value={editable.value}
          onChange={async (e) => {
            const next = e.target.value as string | number;
            setSelectOpen(false);
            setIsEditing(false);
            if (next !== editable.value) await editable.onSave(next);
          }}
          onClose={() => {
            setSelectOpen(false);
            setIsEditing(false);
          }}
          sx={EDITOR_SX}
        >
          {editable.options.map((opt) => (
            <MenuItem key={String(opt.value)} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (editable.type === 'multi-select') {
      return (
        <Select
          autoFocus
          open={selectOpen}
          multiple
          size="small"
          fullWidth
          value={multiDraft}
          onChange={(e) => {
            const raw = e.target.value;
            const next = (Array.isArray(raw) ? raw : [raw]) as (
              | string
              | number
            )[];
            setMultiDraft(next);
          }}
          onClose={async () => {
            setSelectOpen(false);
            setIsEditing(false);
            const changed =
              multiDraft.length !== editable.value.length ||
              multiDraft.some((v, i) => v !== editable.value[i]);
            if (changed) await editable.onSave(multiDraft);
          }}
          sx={EDITOR_SX}
        >
          {editable.options.map((opt) => (
            <MenuItem key={String(opt.value)} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (editable.type === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            {...({ defaultOpen: true } as Record<string, unknown>)}
            enableAccessibleFieldDOMStructure={false}
            // Uncontrolled: the mobile picker keeps a tapped day only in its
            // own value until OK. A controlled `value` with no `onChange`
            // snapped every tap back, so OK saved nothing (SUP-426).
            defaultValue={toMoment(editable.value)}
            minDate={plausibleMinDate()}
            maxDate={plausibleMaxDate()}
            onAccept={async (next) => {
              // A typed two-digit year parses as a real date (0026), so
              // `isValid()` would wave it through — SUP-426. Hold the editor
              // open on an implausible year instead of saving it; the bounds
              // above already put the field in its error state.
              if (next && !isPlausibleDate(next)) return;
              setIsEditing(false);
              await editable.onSave(next ?? null);
            }}
            onClose={() => setIsEditing(false)}
            slotProps={{
              textField: { size: 'small', fullWidth: true, sx: EDITOR_SX },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (editable.type === 'render') {
      return editable.renderEditor({ close: () => setIsEditing(false) });
    }

    return null;
  };

  const hoverBg = isHovered && editable ? 'action.hover' : 'transparent';
  const cellSx = {
    minHeight: 32,
    display: 'flex',
    alignItems: 'center',
    py: 0.25,
    backgroundColor: hoverBg,
    transition: 'background-color 120ms ease',
  } as const;

  const onEnter = () => setIsHovered(true);
  const onLeave = () => setIsHovered(false);

  return (
    <>
      <Box
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        sx={{
          ...cellSx,
          pl: 0.75,
          pr: 1.5,
          ml: spaceFromPrev ? { xs: 0, sm: 2 } : undefined,
          borderTopLeftRadius: 4,
          borderBottomLeftRadius: 4,
        }}
      >
        <Typography
          sx={{
            color: 'text.disabled',
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>
      </Box>
      <Box
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        sx={{
          ...cellSx,
          pr: 0.75,
          gap: 0.75,
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
        }}
      >
        {isEditing ? (
          <Box sx={{ flex: 1 }}>{renderEditor()}</Box>
        ) : (
          <>
            <Box
              onClick={editable ? beginEdit : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                alignSelf: 'stretch',
                cursor: editable ? 'pointer' : 'default',
                color: 'text.primary',
                fontSize: '0.8125rem',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {item.value === null ||
              item.value === undefined ||
              item.value === '' ? (
                <Typography
                  sx={{ fontSize: '0.8125rem' }}
                  color="text.disabled"
                >
                  —
                </Typography>
              ) : typeof item.value === 'string' ||
                typeof item.value === 'number' ? (
                <Typography sx={{ fontSize: '0.8125rem' }}>
                  {item.value}
                </Typography>
              ) : (
                item.value
              )}
            </Box>
            {editable && (
              <IconButton
                size="small"
                onClick={beginEdit}
                sx={{
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 120ms ease',
                  color: 'text.secondary',
                  padding: 0.25,
                }}
                aria-label={`Edit ${item.title}`}
              >
                <EditOutlined fontSize="inherit" />
              </IconButton>
            )}
          </>
        )}
      </Box>
    </>
  );
};

type RenderEntry =
  | {
      kind: 'item';
      row: EditableDataListViewItem;
      spaceFromPrev: boolean;
      key: string;
    }
  | { kind: 'section'; title: string; isFirst: boolean; key: string };

export const EditableDataListView: React.FC<EditableDataListViewProps> = ({
  data,
  sections,
  labelWidth,
  columns = 1,
}) => {
  const labelCol =
    labelWidth === undefined
      ? 'max-content'
      : typeof labelWidth === 'number'
        ? `${labelWidth}px`
        : labelWidth;
  const cols = Math.max(1, Math.floor(columns));

  const entries: RenderEntry[] = [];
  if (sections && sections.length > 0) {
    sections.forEach((section, sIdx) => {
      entries.push({
        kind: 'section',
        title: section.title,
        isFirst: sIdx === 0,
        key: `section-${section.title}-${sIdx}`,
      });
      section.rows.forEach((row, rIdx) => {
        entries.push({
          kind: 'item',
          row,
          spaceFromPrev: cols > 1 && rIdx % cols !== 0,
          key: `${section.title}-${row.title}-${rIdx}`,
        });
      });
    });
  } else if (data) {
    data.forEach((row, index) => {
      entries.push({
        kind: 'item',
        row,
        spaceFromPrev: cols > 1 && index % cols !== 0,
        key: `${row.title}-${index}`,
      });
    });
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `${labelCol} minmax(0, 1fr)`,
          sm:
            cols === 1
              ? `${labelCol} minmax(0, 1fr)`
              : `repeat(${cols}, ${labelCol} minmax(0, 1fr))`,
        },
        alignItems: 'stretch',
        mx: -0.75,
      }}
    >
      {entries.map((entry) =>
        entry.kind === 'section' ? (
          <Box
            key={entry.key}
            sx={{
              gridColumn: '1 / -1',
              pl: 0.75,
              pt: entry.isFirst ? 0 : 2,
              pb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.01em',
                lineHeight: 1,
              }}
            >
              {entry.title}
            </Typography>
          </Box>
        ) : (
          <EditableRow
            key={entry.key}
            item={entry.row}
            spaceFromPrev={entry.spaceFromPrev}
          />
        ),
      )}
    </Box>
  );
};
